import { ErrorModal, HelmetDexteel } from "@dexteel/mesf-core";
import {
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogContentText,
	type DialogProps,
	DialogTitle,
	Typography,
} from "@mui/material";
import Button from "@mui/material/Button";
import Grid2 from "@mui/material/Grid2";
import Snackbar from "@mui/material/Snackbar";
import Alert from "@mui/lab/Alert";
import { type SyntheticEvent, useEffect, useState } from "react";
import { deleteWorkOrders } from "@/pages/work-orders/repositorie/WorkOrdersRepositorie";
import type { ModalWorkOrder } from "../models/ModalWorkOrder";
import { WorkOrderCard } from "@/shared/work-orders-modals/components/work-order-card";

type Props = {
	show: boolean;
	onHide: (onHide: boolean) => void;
	data: ModalWorkOrder[] | null;
	suffixTitle?: string;
};

export function DeleteMultipleWorkOrdersModal({
	show,
	onHide,
	data,
	suffixTitle,
}: Props) {
	const [isSubmitLoading, setIsSubmitLoading] = useState(false);
	const [error, setError] = useState<string>("");
	const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
	const [deleteSuccess, setDeleteSuccess] = useState(false);
	const [fullWidth, setFullWidth] = useState(true);
	const [maxWidth, setMaxWidth] = useState<DialogProps["maxWidth"]>("md");

	const [errorAssetPath, setErrorAssetPath] = useState<string>("");

	//functions
	const handleCancel = () => {
		onHide(false);
	};

	const handleClose = (
		event: Event | React.SyntheticEvent,
		reason?: string,
	) => {
		if (reason === "clickaway") {
			return;
		}
		setDeleteSuccess(false);
	};

	const handleDeleteBtn = async () => {
		setIsSubmitLoading(true);
		const res = await deleteWorkOrders({
			workOrdersCSV: data?.map((item) => item.WorkOrderId).join(",") || "",
			comments: "",
		});
		if (res.ok) {
			setIsSubmitLoading(false);
			setOpenDeleteDialog(false);
			setTimeout(() => {
				setDeleteSuccess(true);
				onHide(true);
			}, 100);
		} else {
			setIsSubmitLoading(false);
			setError(res.message);
		}
	};

	useEffect(() => {
		if (data != null && show) {
		}
		setIsSubmitLoading(false);
	}, [show]);

	return (
		<>
			{show && (
				<HelmetDexteel
					title={`Delete Multiple Work Orders${suffixTitle ? ` - ${suffixTitle}` : ""}`}
				/>
			)}
			<Dialog
				fullWidth={fullWidth}
				maxWidth={maxWidth}
				open={show}
				onClose={() => handleCancel()}
			>
				<DialogTitle
					sx={{
						padding: "15px 24px 10px 24px",
					}}
				>
					<Typography sx={{ fontSize: "1.7em", fontWeight: "bold", margin: 0 }}>
						DELETE MULTIPLE WORK ORDERS
					</Typography>
				</DialogTitle>
				<DialogContent dividers>
					<Typography variant="h6" gutterBottom>
						The following work orders will be deleted:
					</Typography>
					{data?.map((workOrder, index) => (
						<WorkOrderCard key={index} workOrder={workOrder} />
					))}
				</DialogContent>
				<DialogActions
					sx={{ padding: "0px 24px", marginTop: "24px", paddingBottom: "16px" }}
				>
					<Grid2 container spacing={2} justifyContent="flex-end">
						<Grid2 size={{ md: 3, xs: 12 }}>
							<Button
								fullWidth
								variant="contained"
								color="inherit"
								onClick={handleCancel}
							>
								Cancel
							</Button>
						</Grid2>
						<Grid2 size={{ md: 3, xs: 12 }}>
							<Button
								fullWidth
								startIcon={isSubmitLoading && <CircularProgress size="1rem" />}
								disabled={isSubmitLoading}
								variant="contained"
								color="secondary"
								onClick={() => setOpenDeleteDialog(true)}
							>
								Delete
							</Button>
						</Grid2>
					</Grid2>
				</DialogActions>
			</Dialog>
			<Dialog
				open={openDeleteDialog}
				onClose={() => setOpenDeleteDialog(false)}
				aria-labelledby="responsive-dialog-title"
			>
				<DialogTitle id="responsive-dialog-title">WARNING</DialogTitle>
				<DialogContent>
					<DialogContentText sx={{ padding: 0, margin: 0 }}>
						Be careful, once deleted you will not be able to recover these work
						orders, are you sure you want to delete them?
					</DialogContentText>
				</DialogContent>
				<DialogActions sx={{ padding: "16px 24px" }}>
					<Button
						onClick={() => setOpenDeleteDialog(false)}
						autoFocus
						color="inherit"
					>
						CANCEL
					</Button>
					<Button
						autoFocus
						color="secondary"
						startIcon={isSubmitLoading && <CircularProgress size="1rem" />}
						onClick={handleDeleteBtn}
					>
						Delete
					</Button>
				</DialogActions>
			</Dialog>
			<Snackbar
				open={deleteSuccess}
				autoHideDuration={2500}
				onClose={handleClose}
				anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
			>
				<Alert severity="error" onClose={handleClose}>
					Orders deleted
				</Alert>
			</Snackbar>
			<ErrorModal error={error} onHide={() => setError("")} />
			<ErrorModal error={errorAssetPath} onHide={() => setErrorAssetPath("")} />
		</>
	);
}
